import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Stack,
  Divider,
  Avatar,
  Alert,
  IconButton,
  CircularProgress,
} from '@mui/material';
import {
  Person,
  Phone,
  Edit,
  Save,
  Cancel,
  Logout,
  CameraAlt,
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { authAPI } from '../services/api';

const ProfilePage = () => {
  const navigate = useNavigate();
  const { user, logout, updateUser, isAuthenticated, loading: authLoading } = useAuth();

  const [editing, setEditing] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    profilePic: '',
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    // Don't redirect while loading auth state
    if (authLoading) return;

    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    if (user) {
      setFormData({
        name: user.name || '',
        phone: user.phone || '',
        profilePic: user.profilePic || '',
      });
    }
  }, [user, isAuthenticated, authLoading, navigate]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    setError('');
    setSuccess('');
  };

  const handleEdit = () => {
    setEditing(true);
    setError('');
    setSuccess('');
  };

  const handleCancel = () => {
    setEditing(false);
    setFormData({
      name: user.name || '',
      phone: user.phone || '',
      profilePic: user.profilePic || '',
    });
    setError('');
    setSuccess('');
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      setError('Name is required');
      return;
    }

    if (formData.phone && !/^\d{10}$/.test(formData.phone)) {
      setError('Phone must be 10 digits');
      return;
    }

    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const response = await authAPI.updateProfile(formData.name, '', formData.profilePic);
      updateUser(response.data);
      setSuccess('Profile updated successfully!');
      setEditing(false);
    } catch (err) {
      setError(err.message || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleFileSelect = async (event) => {
    const file = event.target.files[0];
    console.log('File selected:', file);
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Please select an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError('Image size should be less than 5MB');
      return;
    }

    setUploading(true);
    setError('');

    try {
      // Convert to base64
      const reader = new FileReader();
      reader.onload = (e) => {
        const base64String = e.target.result;
        console.log('Image converted to base64, length:', base64String.length);
        setFormData(prev => ({ ...prev, profilePic: base64String }));
        setUploading(false);
        setSuccess('Profile picture uploaded! Click Save to update.');
        // Reset file input
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      };
      reader.onerror = () => {
        setError('Failed to read image file');
        setUploading(false);
        // Reset file input
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      };
      reader.readAsDataURL(file);
    } catch (err) {
      console.error('Upload error:', err);
      setError('Failed to upload image');
      setUploading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleProfilePicClick = () => {
    console.log('Profile picture clicked, editing:', editing);
    if (editing) {
      fileInputRef.current?.click();
    } else {
      console.log('Please click Edit Profile first to upload picture');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  if (!user) {
    return null;
  }

  return (
    <Box
      sx={{
        pt: { xs: 9, md: 10 },
        pb: 8,
        minHeight: '100vh',
        bgcolor: '#F8F6F1',
      }}
    >
      <Container maxWidth="md">
        <Typography
          className='font2'
          variant="h3"
          sx={{
            fontFamily: "'Cormorant Garamond', serif",
            fontWeight: 700,
            mb: 4,
            textAlign: 'center',
          }}
        >
          My Profile
        </Typography>

        <Card sx={{ boxShadow: '0 8px 24px rgba(201, 169, 97, 0.15)', mb: 3 }}>
          <CardContent sx={{ p: { xs: 3, md: 4 } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
              <Box sx={{ position: 'relative', mr: 3 }}>
                <Avatar 
                  className='testimonial_icon'
                  src={formData.profilePic || user.profilePic || ''}
                  sx={{
                    width: 80,
                    height: 80,
                    bgcolor: 'primary.main',
                    fontSize: '2rem',
                    cursor: editing ? 'pointer' : 'default',
                    '&:hover': editing ? {
                      opacity: 0.8,
                    } : {},
                  }}
                  onClick={handleProfilePicClick}
                >
                  {!(formData.profilePic || user.profilePic) && user.name?.charAt(0).toUpperCase()}
                </Avatar>
                {editing && (
                  <IconButton
                    sx={{
                      position: 'absolute',
                      bottom: -4,
                      right: -4,
                      bgcolor: 'primary.main',
                      color: 'white',
                      width: 32,
                      height: 32,
                      '&:hover': {
                        bgcolor: 'primary.dark',
                      },
                    }}
                    onClick={handleProfilePicClick}
                    disabled={uploading}
                  >
                    {uploading ? <CircularProgress size={16} /> : <CameraAlt fontSize="small" />}
                  </IconButton>
                )}
              </Box>
              <Box>
                <Typography
                  variant="h5"
                  sx={{
                    fontFamily: "'Cormorant Garamond', serif",
                    fontWeight: 700,
                  }}
                >
                  {user.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {user.countryCode} {user.phone}
                </Typography>
              </Box>
            </Box>
            
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={handleFileSelect}
            />

            <Divider sx={{ my: 3 }} />

            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

            {success && (
              <Alert severity="success" sx={{ mb: 3 }}>
                {success}
              </Alert>
            )}

            <Stack spacing={3}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                  Full Name
                </Typography>
                {editing ? (
                  <TextField
                    fullWidth
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                  />
                ) : (
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Person className='font13' sx={{ mr: 1, color: 'primary.main' }} />
                    <Typography variant="body1">{user.name}</Typography>
                  </Box>
                )}
              </Box>

              <Box>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                  Phone
                </Typography>
                {editing ? (
                  <TextField
                    fullWidth
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="10 digit number"
                  />
                ) : (
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Phone className='font13' sx={{ mr: 1, color: 'primary.main' }} />
                    <Typography variant="body1">
                      {user.countryCode} {user.phone || 'Not provided'}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Stack>

            <Box sx={{ mt: 4, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              {editing ? (
                <>
                  <Button
                    variant="contained"
                    startIcon={<Save />}
                    onClick={handleSave}
                    disabled={saving}
                    sx={{
                      flex: 1,
                      minWidth: 150,
                      borderRadius: 50,
                      background: 'linear-gradient(135deg, #C9A961 0%, #B89850 100%)',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #B89850 0%, #A88740 100%)',
                      },
                    }}
                  >
                    {saving ? 'Saving...' : 'Save Changes'}
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<Cancel />}
                    onClick={handleCancel}
                    sx={{
                      flex: 1,
                      minWidth: 150,
                      borderRadius: 50,
                      borderColor: 'primary.main',
                      color: 'primary.main',
                    }}
                  >
                    Cancel
                  </Button>
                </>
              ) : (
                <Button
                  variant="contained"
                  startIcon={<Edit />}
                  onClick={handleEdit}
                  sx={{
                    flex: 1,
                    minWidth: 150,
                    borderRadius: 50,
                    background: 'linear-gradient(135deg, #C9A961 0%, #B89850 100%)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #B89850 0%, #A88740 100%)',
                    },
                  }}
                >
                  Edit Profile
                </Button>
              )}
            </Box>
          </CardContent>
        </Card>

        <Card sx={{ boxShadow: '0 8px 24px rgba(201, 169, 97, 0.15)' }}>
          <CardContent sx={{ p: { xs: 3, md: 4 } }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Account Actions
            </Typography>
            <Button
              variant="outlined"
              color="error"
              startIcon={<Logout />}
              onClick={handleLogout}
              fullWidth
              sx={{
                borderRadius: 50,
                py: 1.5,
                textTransform: 'none',
                fontWeight: 600,
              }}
            >
              Logout
            </Button>
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
};

export default ProfilePage;
